import React from 'react';

// What has been done in this stage, and by whom.
//
// Every mutating action now writes a TrainingConceptEvent tagged with the stage that
// performed it, so a stage can answer "what happened here" without anyone reading a global
// log. Before this, only four things were recorded at all, so most of what a person did to
// a concept left no trace.
const LABELS = {
  created: 'Concept generated by JPRO',
  regenerated: 'Concept regenerated',
  sources_proposed: 'Sources proposed',
  source_checks_recorded: 'Source checks recorded',
  source_removed: 'Source removed',
  sources_rechecked: 'Source links rechecked',
  rematched: 'Re-matched against the catalog',
  class_target_suggested: 'JPRO worked out a class target',
  class_target_confirmed: 'Class target confirmed',
  routed: 'Routed for review',
  decision: 'Review decided',
  review_decided: 'Review decided', // historical rows
  class_length_changed: 'Class length changed',
  priced: 'Price approved',
  repriced: 'Price corrected',
  override_requested: 'Executive override requested',
  override_signed: 'Override signed',
  production_started: 'Build started',
  artifact_added: 'Artifact added',
  generation_cancelled: 'Build stopped by hand',
  write_back_queued: 'Filing to SharePoint started',
  written_back: 'Filed to SharePoint',
  gate_development: 'Development approved',
  gate_publication: 'Publication approved',
  gate_scheduling: 'Scheduling approved',
};

const label = (e) => LABELS[e.event_type] || String(e.event_type || '').replace(/_/g, ' ');

// The payload carries whatever the action found worth keeping. Rendered as short detail
// rather than raw JSON, and skipped entirely when there is nothing useful in it.
const detail = (e) => {
  const p = e.payload || {};
  const bits = [];
  if (p.added != null) bits.push(`${p.added} added`);
  if (p.unreachable != null) bits.push(`${p.unreachable} unreachable`);
  if (p.source) bits.push(p.source);
  if (p.verdict) bits.push(String(p.verdict).replace(/_/g, ' '));
  if (p.disposition) bits.push(p.disposition === 'revision' ? `revises ${p.target || ''}` : `new class ${p.target || ''}`);
  if (p.kind) bits.push(p.kind);
  if (p.result) bits.push(`match: ${p.result}`);
  if (p.from != null && p.to != null) bits.push(`$${Number(p.from).toLocaleString()} → $${Number(p.to).toLocaleString()}`);
  if (p.inline) bits.push('ran inline (no worker)');
  if (p.resumed) bits.push('resumed');
  if (e.from && e.to && e.from !== e.to) bits.push(`${String(e.from).replace(/_/g, ' ')} → ${String(e.to).replace(/_/g, ' ')}`);
  return bits.filter(Boolean).join(' · ');
};

export default function StageTrail({ events, stage }) {
  const rows = (events || []).filter((e) => e.stage === stage);
  const [open, setOpen] = React.useState(false);

  // Render nothing rather than a claim. This said "nothing has been done in this stage
  // yet" directly beneath a panel listing what had been done, because events recorded
  // before the stage column existed match no stage. The absence of a trail is not evidence
  // that nothing happened.
  if (rows.length === 0) return null;

  // Newest first, and only the last few unless asked: the trail is context, not the point
  // of the stage.
  const ordered = [...rows].reverse();
  const shown = open ? ordered : ordered.slice(0, 3);

  return (
    <div className="border-t border-[#1d2634] pt-2 mt-3">
      <button onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-[10px] uppercase tracking-wide text-[#7d8aa0] mb-1">
        <span>Activity in this stage · {rows.length}</span>
        <span className="text-[#5f6b7e]">{open ? '▾' : '▸'}</span>
      </button>
      {shown.map((e) => (
        <div key={e.id ?? `${e.event_type}-${e.at}`} className="text-[11px] text-[#c7d0e0] py-0.5">
          <div className="flex items-baseline justify-between gap-2">
            <span>{label(e)}</span>
            <span className="text-[10px] text-[#5f6b7e] shrink-0">
              {e.at ? new Date(e.at).toLocaleString() : ''}
            </span>
          </div>
          <div className="text-[10px] text-[#7d8aa0]">
            {[e.actor, detail(e)].filter(Boolean).join(' · ')}
          </div>
        </div>
      ))}
      {!open && ordered.length > shown.length && (
        <button onClick={() => setOpen(true)} className="text-[10px] text-[#5ea9ff] mt-1">
          Show all {ordered.length}
        </button>
      )}
    </div>
  );
}
